import { z } from 'zod';
import { widgetLayoutSchema } from '@/config/schema';
import { useWidgetLayout } from '@/hooks/useWidgetLayout';
import { assetUrl, IMG } from '@/lib/assets';
import { configuredOr } from '@/lib/configValue';
import { fontStyleToCss, type FontOptions } from '@/lib/fontStyle';
import { warnOnce } from '@/lib/warnOnce';
import type { WidgetProps } from '@/widgets/registry';

/**
 * A centred row of images, each with a label beside it. Port of `imageLabelWidget.vue`.
 *
 * All seven deployed configs use it for the same thing: the two app-store badges, with the
 * label to the left of the first and to the right of the second.
 */

/**
 * [sic] `size` / `family` / `color` / `weight`, **not** the `fontSize` / `fontFamily` /
 * `fontColor` / `fontWeight` spelling every other widget uses. Config keys are immovable
 * (CLAUDE.md constraint 1), so the difference is absorbed here — at the schema boundary —
 * and the rest of the widget works with the ordinary `FontOptions` shape.
 */
const itemFontSchema = z
  .object({
    size: z.union([z.number(), z.string()]).optional(),
    family: z.string().optional(),
    color: z.string().optional(),
    weight: z.string().optional(),
  })
  .transform(
    (font): FontOptions => ({
      fontSize: font.size,
      fontFamily: font.family,
      fontColor: font.color,
      fontWeight: font.weight,
    }),
  );

const itemsLayoutSchema = z.object({
  /** Gap between items, rem. Default 0.5. */
  spacing: z.number().optional(),
  /** Background of the *label*, not of the widget. Default transparent. */
  backgroundColor: z.string().optional(),
  /** Gap between an item's label and its image, rem. Default 0 — see below. */
  itemLabelSpacing: z.number().optional(),
  /** rem. Default 3. */
  imageWidth: z.number().optional(),
  font: itemFontSchema.optional(),
});

const itemSchema = z.object({
  /** Resolved from `img/` — this widget's base directory, not `img/logos/`. */
  img: z.string().optional(),
  label: z.string().default(''),
  /** `right` puts the label on the left of the image, via `flex-row-reverse`. */
  labelPosition: z.string().default('right'),
});

const optionsSchema = z.object({
  layout: widgetLayoutSchema.optional(),
  items: z.array(itemSchema).default([]),
  itemsLayout: itemsLayoutSchema.optional(),
});

export function ImageLabelWidget({ componentId, options }: WidgetProps) {
  const parsed = optionsSchema.safeParse(options);
  // Hooks run before any early return.
  const opts = parsed.success ? parsed.data : undefined;
  const { className, style } = useWidgetLayout(opts?.layout);

  if (!parsed.success) {
    warnOnce(
      `image-label-options:${componentId}`,
      '[signage] ImageLabelWidget options rejected; rendering nothing. %s',
      z.prettifyError(parsed.error),
    );
    return null;
  }

  // `parsed.data` rather than `opts`: TypeScript does not relate the narrowing of `parsed` to
  // the separate const the hooks above had to read from.
  const { items, itemsLayout } = parsed.data;

  /*
   * `itemLabelSpacing` defaults to **0**, not to the `0.2rem` the legacy initialiser reads.
   * Legacy set `0.2rem` at declaration and then overwrote it with `0rem` inside
   * `if (options.itemsLayout)` — a branch that runs for every deployed config — so `0.2rem`
   * was only ever reachable by a config with items and no `itemsLayout`, of which there are
   * none. Taking the branch's value is what keeps the current rendering.
   */
  const gap = `${String(itemsLayout?.spacing ?? 0.5)}rem`;
  const labelGap = `${String(itemsLayout?.itemLabelSpacing ?? 0)}rem`;
  const imageWidth = `${String(itemsLayout?.imageWidth ?? 3)}rem`;
  const labelStyle = {
    ...fontStyleToCss(itemsLayout?.font, { fontSize: '0.5rem' }),
    padding: '0.1rem',
    background: configuredOr(itemsLayout?.backgroundColor, 'transparent'),
  };

  return (
    <div className={className} style={style}>
      <div className="image-label-container flex flex-row w-full justify-center" style={{ gap }}>
        {items.map((item, index) => {
          const imageUrl = item.img ? assetUrl(`${IMG.root}${item.img}`) : undefined;
          const reversed = item.labelPosition === 'right';

          return (
            /*
             * The legacy class name is derived, not assigned back onto the config node.
             * `imageLabelWidget.vue` did `element.class = 'item ' + position` during setup
             * (W-11), which mutates an object the config module hands out by reference and
             * SYM-492's slider re-reads on every slide change.
             *
             * `key` is the index because these items have no id and the array is immutable
             * for the lifetime of the page — the config never changes after `loadConfig`.
             */
            <div
              key={index}
              className={`item ${item.labelPosition} flex items-center ${reversed ? 'flex-row-reverse' : 'flex-row'}`}
              style={{ gap: labelGap }}
            >
              <span className="label" style={labelStyle}>
                {item.label}
              </span>
              {imageUrl !== undefined && (
                <img
                  src={imageUrl}
                  className="item-image h-auto"
                  style={{ width: imageWidth }}
                  alt={item.label}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
